/**
 * Button Helpers
 *
 * Reusable button helper components with internationalization support.
 * Used by Modal and Slideout components for consistent button styling and behavior.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Button } from '@/components/_untitled/base/buttons/button';
import { prefixes, useTypedTranslation } from "@/locales";

// Common types
type ButtonColor =
    'primary'
    | 'secondary'
    | 'tertiary'
    | 'primary-destructive'
    | 'secondary-destructive'
    | 'tertiary-destructive'
    | 'link-gray'
    | 'link-color'
    | 'link-destructive';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

// Shared interface for components that can be closed
export interface ComponentWithClose {
  onClose?: () => void;
}

type ButtonLabel =
    'search'
    | 'save'
    | 'cancel'
    | 'delete'
    | 'reject'
    | 'continue'
    | 'close'
    | 'dismiss'
    | 'dismissNotification'
    | 'back'
    | 'skip'
    | 'confirmDelete'
    | 'loading'
    | 'uploading';

// Base button interface and component
interface BaseButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  onAction?: () => boolean | void | Promise<boolean | void>;
  children?: ReactNode;
  color?: ButtonColor;
  size?: ButtonSize;
  onClose?: () => void;
  translationKey?: ButtonLabel;
  isLoading?: boolean;
}

const BaseButton = ({
                      onAction,
                      children,
                      color,
                      size = "md",
                      onClose,
                      translationKey,
                      isLoading,
                      disabled,
                      ...props
                    }: BaseButtonProps) => {
  const [tGeneral] = useTypedTranslation(prefixes('general'));

  // Check if this button should submit a form instead of using onClick
  const isFormSubmit = 'form' in props && props.form;

  const handleClick = async () => {
    if (!onAction) {
      // No action provided - just close (common for Cancel buttons)
      onClose?.();
      return;
    }

    try {
      const result = await onAction();

      // If result is explicitly false, don't close
      // If result is true or undefined, close
      if (result !== false) {
        onClose?.();
      }
    } catch (error) {
      console.error('Button action failed:', error);
      // Don't close on error
    }
  };

  // If button has form attribute, use type="submit" instead of onClick
  const buttonProps = isFormSubmit
      ? { type: "submit" as const, ...props }
      : { onClick: handleClick, ...props };

  return (
      <Button color={ color } size={ size } className="w-full" isLoading={ isLoading }
              isDisabled={ disabled } { ...buttonProps }>
        { children || (translationKey && tGeneral(translationKey)) }
      </Button>
  );
};

// Specific button interfaces and implementations
interface SaveButtonProps extends Omit<BaseButtonProps, 'onAction' | 'translationKey'> {
  onSave?: () => boolean | void | Promise<boolean | void>;
}

interface CancelButtonProps extends Omit<BaseButtonProps, 'onAction' | 'translationKey'> {
  onCancel?: () => boolean | void | Promise<boolean | void>;
}

interface DeleteButtonProps extends Omit<BaseButtonProps, 'onAction' | 'translationKey'> {
  onDelete?: () => boolean | void | Promise<boolean | void>;
}

interface RejectButtonProps extends Omit<BaseButtonProps, 'onAction' | 'translationKey'> {
    onReject?: () => boolean | void | Promise<boolean | void>;
}

export const SaveButton = ({ onSave, isLoading, ...props }: SaveButtonProps) => {
  return (
      <BaseButton
          onAction={ onSave }
          color="primary"
          translationKey="save"
          isLoading={ isLoading }
          { ...props }
      />
  );
};

export const CancelButton = ({ onCancel, ...props }: CancelButtonProps) => {
  return (
      <BaseButton
          onAction={ onCancel }
          color="secondary"
          translationKey="cancel"
          { ...props }
      />
  );
};

export const DeleteButton = ({ onDelete, ...props }: DeleteButtonProps) => {
  return (
      <BaseButton
          onAction={ onDelete }
          color="primary-destructive"
          translationKey="delete"
          { ...props }
      />
  );
};

export const RejectButton = ({ onReject, ...props }: RejectButtonProps) => {
    return (
        <BaseButton
            onAction={ onReject }
            color="primary-destructive"
            translationKey="reject"
            { ...props }
        />
    );
};